import {
  GenericMessageDescriptor,
  useFormatMessageGeneric,
} from '@main/core-ui';
import { map } from 'bluebird';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';
import uniqBy from 'lodash/uniqBy';
import React, { useRef, useState } from 'react';

import { FilePicker, FilePickerProps, UploadErrorMap } from '../FilePicker';
import { uploadMessages } from '../FilePicker/messages';
import { uploadFileRaw } from './uploadFileRaw';

/**
 * the result of the transformation, with some extra metadata
 */
interface TransformedFileResult {
  /** the updated file */
  file: any;
  /** any header overrides to be spread before making the request */
  headerOverrides?: Record<string, string | undefined>;
  /** updated filename */
  filename?: string;
  /** should we send the file as raw bytes or wrap with FormData? */
  sendRaw?: boolean;
  /** does the result have any validation errors? */
  validationError?: GenericMessageDescriptor;
}

/**
 * handler to transform the file before sending the xhr request
 */
type TransformFileHandler = (
  file: File,
) => TransformedFileResult | Promise<TransformedFileResult>;

export interface FilePickerUploadProps
  extends Omit<FilePickerProps, 'files' | 'onChange' | 'onAbort' | 'onSubmit'> {
  /** when uploading multiple files, how many can we upload simultaneously */
  uploadConcurrency?: number;
  /** the url to upload to */
  targetUrl: string;
  /** the HTTP method to use when uploading */
  method?: 'POST' | 'PUT';
  /** the headers to add to the request */
  headers?: Record<string, string>;
  /** success handler */
  onSuccess?: (
    files: (File & {
      /** the response from the server */
      responseBody: any;
    })[],
  ) => void;
  /** error handler  */
  onError?: (error: Error) => void;
  /**
   * function to transform a file before uploading it. Returns the
   * transformed file and any necessary header overrides to include in the
   * final request
   */
  transformFile?: TransformFileHandler;
}

const DEFAULT_UPLOAD_CONCURRENCY = 2;

/**
 * Upload
 */
export const FilePickerUpload: React.FC<FilePickerUploadProps> = ({
  targetUrl,
  method,
  headers,
  onSuccess,
  transformFile,
  onError,
  uploadConcurrency,
  ...filePickerProps
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const { formatMessageGeneric } = useFormatMessageGeneric();
  const [abort, setAbort] = useState<Record<string, () => void>>({});
  const [loading, setLoading] = useState(false);
  const [progressMapBytes, setProgressMapBytes] = useState({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadResponseBodies, setUploadResponseBodies] = useState<
    Record<string, any>
  >({});

  const doUpload = (
    setErrors: React.Dispatch<React.SetStateAction<UploadErrorMap>>,
  ): void => {
    setLoading(true);
    clearProgress();
    const uploadOrder = orderBy(files, ['size'], ['asc']);
    const transformFileOrDefault: TransformFileHandler =
      transformFile ?? ((file) => ({ file }));

    map(
      uploadOrder,
      (file) =>
        new Promise<{
          /** the error for the request */
          error: Error | undefined;
          /** the file for the request */
          file: File;
          /** the body of the response */
          body: any;
        }>((resolve) => {
          Promise.resolve(transformFileOrDefault(file)).then((transformed) => {
            if (transformed.validationError) {
              setProgressMapBytes((curr) => ({
                ...curr,
                [file.name]: file.size,
              }));
              const prettyError = formatMessageGeneric(
                transformed.validationError,
              ) as string;
              setErrors((curr) => ({
                ...curr,
                [file.name]: prettyError,
              }));
              onError?.(new Error(prettyError));
              resolve({ error: new Error(prettyError), file, body: {} });
              return;
            }
            const abort = uploadFileRaw({
              file: transformed.file ?? file,
              method: method ?? 'POST',
              action: targetUrl,
              filename:
                transformed.filename ?? transformed.file?.name ?? file.name,
              headers: {
                ...headers,
                ...(transformed.headerOverrides ?? {}),
              },
              asForm: !transformed.sendRaw,
              withCredentials: true,
              onProgress: (event) => {
                setProgressMapBytes((curr) => ({
                  ...curr,
                  [file.name]: event.loaded,
                }));
              },
              onError: (error, body) => {
                setProgressMapBytes((curr) => ({
                  ...curr,
                  [file.name]: file.size,
                }));
                setErrors((curr) => ({
                  ...curr,
                  [file.name]: formatMessageGeneric(
                    uploadMessages.unexpectedError,
                    {
                      error: error.message,
                    },
                  ) as string,
                }));
                onError?.(error);
                resolve({ error, file, body });
              },
              onSuccess: (body) => {
                setProgressMapBytes((curr) => ({
                  ...curr,
                  [file.name]: file.size,
                }));
                setUploadResponseBodies((curr) => ({
                  ...curr,
                  [file.name]: body,
                }));
                resolve({ file, body, error: undefined });
              },
            });
            setAbort((curr) => ({
              ...curr,
              [file.name]: abort.abort,
            }));
          });
        }),
      {
        concurrency: Math.max(
          uploadConcurrency ?? DEFAULT_UPLOAD_CONCURRENCY,
          1,
        ),
      },
    ).then((results) => {
      setLoading(false);
      // if any uploads completed
      const [failedFiles, finishedFiles] = partition(
        results,
        ({ error }) => !!error,
      );
      if (failedFiles.length < files.length) {
        // still call success to trigger any necessary refetches for completed
        // files but don't clear entire file list, just completed items
        onSuccess?.(
          finishedFiles.map(({ file, body }) =>
            Object.assign(file, {
              responseBody: body,
            }),
          ),
        );
        setFiles(failedFiles.map(({ file }) => file));
      }
      clearProgress();
      clearInputSelectedFiles();
    });
  };
  const clearProgress = (): void => {
    setProgressMapBytes({});
  };
  const clearInputSelectedFiles = (): void => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <FilePicker
      files={files}
      onChange={(newFiles) => {
        clearProgress();

        if (newFiles.length > 0) {
          setFiles(orderBy(uniqBy(newFiles, 'name'), 'name'));
        }
      }}
      onAbort={(errors) => {
        // if any uploads completed
        const [failedFiles, finishedFiles] = partition(
          files,
          (file) =>
            (progressMapBytes[file.name] ?? 0) < file.size || errors[file.name],
        );
        if (failedFiles.length < files.length) {
          // still call success to trigger any necessary refetches for completed
          // files but don't clear entire file list, just completed items
          onSuccess?.(
            finishedFiles.map((file) =>
              Object.assign(file, {
                responseBody: uploadResponseBodies[file.name],
              }),
            ),
          );
          setFiles(failedFiles);
        }
        failedFiles.forEach((file) => abort[file.name]?.());
        setLoading(false);
        clearInputSelectedFiles();
        clearProgress();
      }}
      onSubmit={(_, setErrors) => doUpload(setErrors)}
      progressMapBytes={progressMapBytes}
      loading={loading}
      {...filePickerProps}
    />
  );
};
